import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';

// 우선순위 옵션 (주황색 톤온톤 적용 가능하도록 구조 유지)
const PRIORITY_OPTIONS = [
  { value: 1, label: '긴급' },
  { value: 2, label: '보통' },
  { value: 3, label: '낮음' },
];

// 상태 옵션
const STATUS_OPTIONS = [
  {
    value: '10',
    label: '대기',
    color: 'bg-gray-100 text-gray-600 border-gray-200',
  },
  {
    value: '20',
    label: '진행중',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  {
    value: '90',
    label: '완료',
    color: 'bg-green-100 text-green-700 border-green-200',
  },
];

const EMPTY_FORM = {
  title: '',
  contents: '',
  priorityBit: 2,
  statusCd: '10',
  startDt: dayjs().format('YYYY-MM-DD'),
  dueDt: dayjs().format('YYYY-MM-DD'),
  complDt: '',
};

function TaskFormDialog({
  open = false,
  onClose = () => {},
  onSave = () => {},
  onDelete,
  initialData = null,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const dropdownRef = useRef(null);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsStatusOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 데이터 초기화 및 모드 전환 (추가/수정)
  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setForm({
        title: initialData.title || '',
        contents: initialData.contents || initialData.description || '',
        priorityBit: Number(initialData.priorityBit ?? 2),
        statusCd: String(initialData.statusCd || '10'),
        startDt:
          initialData.startDt?.substr(0, 10) || dayjs().format('YYYY-MM-DD'),
        dueDt: initialData.dueDt?.substr(0, 10) || dayjs().format('YYYY-MM-DD'),
        complDt: initialData.complDt || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, initialData]);

  if (!open) return null;

  const isEdit = !!initialData?.workId;
  const currentStatus =
    STATUS_OPTIONS.find((opt) => opt.value === form.statusCd) ||
    STATUS_OPTIONS[0];

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!form.title.trim()) return;
    onSave(form, isEdit ? initialData.workId : '');
  };

  // 공통 스타일 클래스
  const inputCls =
    'w-full bg-gray-50/50 border border-[#e1e8ed] rounded-lg px-3 py-2.5 text-sm font-semibold text-[#2c3e50] placeholder-gray-300 focus:outline-none focus:bg-white focus:border-[#77b054] focus:ring-1 focus:ring-[#77b054]/30 transition-colors';
  const labelCls =
    'block text-xs font-bold text-[#7f8c8d] uppercase tracking-wider mb-1';

  return (
    <div
      className="fixed inset-0 z-modal overflow-y-auto flex min-h-full items-center justify-center p-4 py-8"
      style={{
        backgroundColor: 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(2px)',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl flex flex-col overflow-visible my-auto">
        {/* 헤더 */}
        <div className="px-6 py-4 border-b border-[#e1e8ed] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center text-[#77b054]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </div>
            <h3 className="text-base font-bold text-[#2c3e50]">
              {isEdit ? '업무 상세 내역' : '새 업무 추가'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-[#7f8c8d] hover:text-[#2c3e50] transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* 본문 */}
        <div className="px-6 py-5 flex-1 space-y-4">
          {/* 업무 제목 */}
          <div>
            <label className={labelCls}>
              업무 제목 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="업무 제목을 입력하세요"
              className={inputCls}
            />
          </div>

          {/* 업무 내용 */}
          <div>
            <label className={labelCls}>업무 내용</label>
            <textarea
              value={form.contents}
              onChange={(e) => handleChange('contents', e.target.value)}
              placeholder="업무 상세 내용을 입력하세요"
              rows={2}
              className={`${inputCls} resize-none`}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* 우선순위 - 주황색 포인트 적용 */}
            <div>
              <label className={labelCls}>우선순위</label>
              <div className="flex gap-1 p-1 bg-gray-100/50 rounded-lg border border-[#e1e8ed]">
                {PRIORITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleChange('priorityBit', opt.value)}
                    className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${form.priorityBit === opt.value ? 'bg-[#ffa246] text-white shadow-sm' : 'text-[#7f8c8d] hover:text-[#2c3e50]'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 진행 상태 드롭다운 (아래로 열림) */}
            <div className="relative" ref={dropdownRef}>
              <label className={labelCls}>진행 상태</label>
              <button
                type="button"
                onClick={() => setIsStatusOpen(!isStatusOpen)}
                className={`flex items-center justify-between w-full px-3 py-[6.5px] bg-white border border-[#e1e8ed] rounded-lg hover:border-[#77b054]/50 transition-all shadow-sm group`}
              >
                <span
                  className={`px-2 py-0.5 rounded-full border text-xs font-bold ${currentStatus.color}`}
                >
                  {currentStatus.label}
                </span>
                <svg
                  className={`w-3.5 h-3.5 text-[#7f8c8d] transition-transform duration-200 ${isStatusOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isStatusOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 z-[1100] bg-white border border-[#e1e8ed] rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  {STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        handleChange('statusCd', opt.value);
                        setIsStatusOpen(false);
                      }}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                    >
                      <span
                        className={`px-2 py-0.5 rounded-full border text-xs font-bold ${opt.color}`}
                      >
                        {opt.label}
                      </span>
                      {form.statusCd === opt.value && (
                        <svg
                          className="w-3.5 h-3.5 text-[#77b054]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 시작 예정일 · 완료 기한
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>시작 예정일</label>
              <input
                type="date"
                value={form.startDt}
                onChange={(e) => handleChange('startDt', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>완료 기한</label>
              <input
                type="date"
                value={form.dueDt}
                onChange={(e) => handleChange('dueDt', e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
          */}

          {/* 실제 완료 일시 · 등록자
          <div className="pt-2 grid grid-cols-2 gap-4 border-t border-[#e1e8ed]/60">
            <div>
              <label className={labelCls}>실제 완료 일시</label>
              <div className={readonlyCls}>{form.complDt || '— 미완료 —'}</div>
            </div>
            <div>
              <label className={labelCls}>등록자</label>
              <div className={readonlyCls}>
                <div className="w-4 h-4 rounded-full bg-[#c5c5c5] text-white flex items-center justify-center shrink-0 overflow-hidden">
                  {displayUserPhoto ? (
                    <img
                      src={displayUserPhoto}
                      alt={displayUserName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Icon.User className="w-2.5 h-2.5" />
                  )}
                </div>
                <span className="text-xs font-semibold text-[#7f8c8d]">
                  {displayUserName || '—'}
                </span>
              </div>
            </div>
          </div>
          */}
        </div>

        {/* 푸터 */}
        <div className="px-6 py-4 border-t border-[#e1e8ed] bg-gray-50/50 flex items-center justify-between gap-2 shrink-0">
          <div>
            {isEdit && typeof onDelete === 'function' && (
              <button
                type="button"
                onClick={onDelete}
                className="px-5 py-2 rounded-lg text-sm font-bold bg-white text-red-500 border border-red-200 hover:bg-red-50 transition-colors"
              >
                삭제
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-lg text-sm font-bold bg-white text-[#7f8c8d] border border-[#e1e8ed] hover:bg-gray-100 transition-colors"
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!form.title.trim()}
              className="px-5 py-2 rounded-lg text-sm font-bold bg-[#77b054] text-white hover:bg-[#5d9040] transition-colors disabled:opacity-40"
            >
              {isEdit ? '저장' : '업무 추가'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TaskFormDialog;
